import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useLanguage } from '../contexts/LanguageContext';
import { langToSlug } from '../hooks/useLocalePath';
import { renderT } from '../i18n/renderT';
import type { TranslationKey } from '../i18n';
import DeliverableModals from '../components/features/DeliverableModals';

/* ═══════════════════════════════════════════════════════════
   MODAL MAP — Maps example names to modal overlay IDs
═══════════════════════════════════════════════════════════ */
const MODAL_MAP: Record<string, string> = {
    'Value Pools Map': 'preview-value-pools-map',
    'AI Ambition Statement': 'preview-ambition-statement',
    'Metrics Framework': 'preview-metrics-framework',
    'AI Maturity Score Report': 'preview-maturity-score',
    'Capability Heatmap': 'preview-capability-heatmap',
    'Gap Analysis Report': 'preview-gap-analysis',
    'Impact × Feasibility Matrix': 'preview-impact-feasibility',
    'Use Case Business Cases': 'preview-usecase-business-cases',
    'Quick Win Roadmap': 'preview-quick-win-roadmap',
    'Architecture Blueprint': 'preview-architecture-blueprint',
    'Financial Impact Model': 'preview-financial-impact',
    '18-Month Roadmap': 'preview-18month-roadmap',
    'Lakehouse Architecture': 'preview-lakehouse-architecture',
    'Data Catalog': 'preview-data-catalog',
    'Pipeline Monitoring': 'preview-pipeline-monitoring',
    'Model Performance Dashboard': 'preview-model-performance',
    'MLOps Pipeline Docs': 'preview-mlops-pipeline',
    'Adoption Playbook': 'preview-adoption-playbook',
    'Agent Architecture Doc': 'preview-agent-architecture',
    'Agent Monitoring': 'preview-agent-monitoring',
    'Security Guardrails': 'preview-security-guardrails',
    'Operating Model Blueprint': 'preview-operating-model',
    'RACI Matrix': 'preview-raci-matrix',
    'AI Governance Policies': 'preview-governance-policies',
    'ROI Dashboard': 'preview-roi-dashboard',
    'Executive Scorecard': 'preview-executive-scorecard',
    'Value Realization Report': 'preview-value-realization',
};

/* ═══════════════════════════════════════════════════════════
   DATA — Phase 1 Steps
═══════════════════════════════════════════════════════════ */
const PHASE1_STEPS = [
    {
        id: 'p1s1', num: '01', timing: 'Week 1',
        title: 'Executive Alignment & Ambition',
        icon: (
            <svg viewBox="0 0 24 24"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
        ),
        detail: {
            frames: [
                { tag: 'Inputs', items: ['C-suite and business leaders available for workshops', 'Overview of key business challenges and strategic priorities', 'Inventory of ongoing AI and data initiatives', 'Strategic priorities for the next 18–36 months'] },
                { tag: 'Key Activities', items: ['Executive alignment workshops with C-suite stakeholders', 'Value pool mapping across every business area', 'Target metrics and KPI definition tied to P&L', 'Governance principles establishment for AI initiatives'] },
                { tag: 'Key Deliverables', items: ['AI Ambition Statement aligned to corporate strategy', 'Value Pools Map by business area and function', 'Target Metrics Framework with baseline benchmarks', 'Governance Principles for AI decision-making'] },
                { tag: 'What You Get', value: 'Executive clarity before a single line of code.', desc: 'Your leadership team aligns on exactly where AI can move the P&L — not vague aspirations, but a focused mandate that drives every Discovery decision forward.' },
            ],
            examples: [
                { name: 'Value Pools Map', ext: 'preview', iconType: 'doc' },
                { name: 'AI Ambition Statement', ext: 'preview', iconType: 'doc' },
                { name: 'Metrics Framework', ext: 'preview', iconType: 'grid' },
            ],
        },
    },
    {
        id: 'p1s2', num: '02', timing: 'Weeks 2–4',
        title: 'AI & Data Maturity Assessment',
        icon: (
            <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><path d="M12 8v4l3 3"/><path d="M3.05 11a9 9 0 0 1 17.9 0"/></svg>
        ),
        detail: {
            frames: [
                { tag: 'Inputs', items: ['Access to data systems, technical stack and infrastructure', 'Data, engineering and analytics team leaders', 'Existing architecture documentation and data dictionaries', 'History of past data and AI initiatives and their outcomes'] },
                { tag: 'Key Activities', items: ['Comprehensive technical data and infrastructure assessment', 'Capability interviews across data, ML and engineering teams', 'Data quality audit and governance maturity evaluation', 'Gap analysis across 5 maturity dimensions with scoring'] },
                { tag: 'Key Deliverables', items: ['AI Maturity Score™ (0–5.0 per dimension)', 'Gap Analysis Report with remediation priorities', 'Capability Heatmap across teams and functions', 'Prioritized Pain Points linked to business impact'] },
                { tag: 'What You Get', value: 'An honest diagnosis — not a sales pitch.', desc: 'Your AI Maturity Score becomes the definitive baseline every future data and AI investment decision will reference.' },
            ],
            examples: [
                { name: 'AI Maturity Score Report', ext: 'preview', iconType: 'doc' },
                { name: 'Capability Heatmap', ext: 'preview', iconType: 'grid' },
                { name: 'Gap Analysis Report', ext: 'preview', iconType: 'doc' },
            ],
        },
    },
    {
        id: 'p1s3', num: '03', timing: 'Weeks 4–6',
        title: 'Use Case Identification & Prioritization',
        icon: (
            <svg viewBox="0 0 24 24"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>
        ),
        detail: {
            frames: [
                { tag: 'Inputs', items: ['AI Maturity Score output and gap analysis findings', 'Business unit leaders across revenue and operations', 'P&L data by process, department and business area', 'Industry AI adoption benchmarks and competitive intelligence'] },
                { tag: 'Key Activities', items: ['Cross-functional use case ideation workshops', 'Financial impact modeling per candidate use case', 'Technical feasibility and data readiness scoring', 'Quick wins vs. strategic bets classification'] },
                { tag: 'Key Deliverables', items: ['Full Use Case Portfolio (20–30 candidates identified)', 'Impact × Feasibility Prioritization Matrix', 'Detailed Business Cases for Top 5–10 use cases', 'Quick Win Roadmap for immediate value capture'] },
                { tag: 'What You Get', value: 'A ranked portfolio of AI bets by real economic impact', desc: " — not what's technically interesting, but what moves the needle for your business." },
            ],
            examples: [
                { name: 'Impact × Feasibility Matrix', ext: 'preview', iconType: 'grid' },
                { name: 'Use Case Business Cases', ext: 'preview', iconType: 'doc' },
                { name: 'Quick Win Roadmap', ext: 'preview', iconType: 'chat' },
            ],
        },
    },
    {
        id: 'p1s4', num: '04', timing: 'Weeks 6–10',
        title: 'Target Architecture & Roadmap',
        icon: (
            <svg viewBox="0 0 24 24"><path d="M3 3h18v4H3z"/><path d="M3 10h12v4H3z"/><path d="M3 17h8v4H3z"/></svg>
        ),
        detail: {
            frames: [
                { tag: 'Inputs', items: ['Prioritized use cases with validated business cases', 'Current technology stack and infrastructure landscape', 'Budget parameters and investment appetite', 'Regulatory, compliance and security constraints'] },
                { tag: 'Key Activities', items: ['Target architecture design across data, ML and AI layers', 'Technology evaluation, selection and vendor mapping', 'Phase sequencing with dependency and risk analysis', 'Investment vs. return modeling over 18–36 months'] },
                { tag: 'Key Deliverables', items: ['Architecture Blueprint (data + AI + agent layers)', '18–36 Month Roadmap — sequenced, costed, board-ready', 'Financial Impact Model with ROI projections', 'Industrialization Plan for Phase 2 execution'] },
                { tag: 'What You Get', value: 'A board-ready plan with the numbers behind it.', desc: ' Your team knows exactly what to build, in what order, and what returns to expect — ready for executive approval.' },
            ],
            examples: [
                { name: 'Architecture Blueprint', ext: 'preview', iconType: 'doc' },
                { name: 'Financial Impact Model', ext: 'preview', iconType: 'grid' },
                { name: '18-Month Roadmap', ext: 'preview', iconType: 'chat' },
            ],
        },
    },
];

/* ═══════════════════════════════════════════════════════════
   DATA — Phase 2 Modules
═══════════════════════════════════════════════════════════ */
const PHASE2_MODULES = [
    {
        id: 'p2sa', code: 'A',
        title: 'Data Foundation Modernization',
        timing: 'Lakehouse · Pipelines · Governance',
        icon: (
            <svg viewBox="0 0 24 24"><ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M21 12c0 1.66-4.03 3-9 3S3 13.66 3 12"/><path d="M3 5v14c0 1.66 4.03 3 9 3s9-1.34 9-3V5"/></svg>
        ),
        detail: {
            frames: [
                { tag: 'Key Activities', items: ['Lakehouse and data products architecture implementation', 'Data pipeline design, build and orchestration (batch + streaming)', 'Governance, security, data cataloging and lineage documentation', 'Observability, data quality monitoring and alerting in production'] },
                { tag: 'Key Deliverables', items: ['Data platform in production with defined SLAs', 'Data Catalog with documented lineage and ownership', 'Monitored pipelines with alerting and auto-healing', 'Governance policies implemented, auditable and enforced'] },
                { tag: 'Inputs Required', items: ['Architecture Blueprint from Discovery', 'Current data infrastructure fully mapped', 'Data requirements from prioritized use cases', 'Security, compliance and regulatory policies'] },
                { tag: 'What You Get', value: 'AI that works requires data that flows.', desc: ' We build the foundation every use case depends on — reliable, traceable, governed, and ready to scale across the organization.' },
            ],
            examples: [
                { name: 'Lakehouse Architecture', ext: 'preview', iconType: 'doc' },
                { name: 'Data Catalog', ext: 'preview', iconType: 'grid' },
                { name: 'Pipeline Monitoring', ext: 'preview', iconType: 'chart' },
            ],
        },
    },
    {
        id: 'p2sb', code: 'B',
        title: 'Priority Use Case Implementation',
        timing: 'MLOps · Production · Adoption',
        icon: (
            <svg viewBox="0 0 24 24"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
        ),
        detail: {
            frames: [
                { tag: 'Key Activities', items: ['Production-grade model development and fine-tuning', 'MLOps / LLMOps pipeline setup with CI/CD automation', 'Business systems integration and API deployment', 'Monitoring, drift detection and user adoption support'] },
                { tag: 'Key Deliverables', items: ['AI models in production (not demos, not POCs)', 'Operational MLOps pipeline with CI/CD', 'Performance monitoring dashboards', 'User adoption playbook and training materials'] },
                { tag: 'Inputs Required', items: ['Top use cases prioritized in Discovery', 'Data foundation in production (Module A)', 'Baseline metrics per use case', 'Integration requirements with core systems'] },
                { tag: 'What You Get', value: 'Not demos. Production systems.', desc: ' Revenue, margin and efficiency gains you can measure and report to the board — every sprint, every month.' },
            ],
            examples: [
                { name: 'Model Performance Dashboard', ext: 'preview', iconType: 'chart' },
                { name: 'MLOps Pipeline Docs', ext: 'preview', iconType: 'doc' },
                { name: 'Adoption Playbook', ext: 'preview', iconType: 'doc' },
            ],
        },
    },
    {
        id: 'p2sc', code: 'C',
        title: 'Agent Architecture Deployment',
        timing: 'Orchestration · Guardrails · Integration',
        icon: (
            <svg viewBox="0 0 24 24"><path d="M12 2a2 2 0 0 1 2 2c0 .74-.4 1.39-1 1.73V7h1a7 7 0 0 1 7 7h1a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1h-1v1a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-1H2a1 1 0 0 1-1-1v-3a1 1 0 0 1 1-1h1a7 7 0 0 1 7-7h1V5.73A2 2 0 0 1 10 4a2 2 0 0 1 2-2z"/></svg>
        ),
        detail: {
            frames: [
                { tag: 'Key Activities', items: ['AI agent design and multi-agent orchestration', 'Integration with core business systems and workflows', 'Security guardrails, access control and audit trails', 'Observability, logging and performance optimization'] },
                { tag: 'Key Deliverables', items: ['Production AI agents with orchestration layer', 'Agent monitoring and observability dashboards', 'Security framework with guardrails and audit trails', 'Integration documentation and operational runbooks'] },
                { tag: 'Inputs Required', items: ['Agent use cases identified in Discovery', 'Business workflow mappings and process documentation', 'Security, compliance and regulatory requirements', 'System integration specifications and API contracts'] },
                { tag: 'What You Get', value: 'AI agents built to operate, not just demo.', desc: ' Autonomous systems that eliminate repetitive work, accelerate decisions, and integrate safely into your enterprise workflows.' },
            ],
            examples: [
                { name: 'Agent Architecture Doc', ext: 'preview', iconType: 'doc' },
                { name: 'Agent Monitoring', ext: 'preview', iconType: 'chart' },
                { name: 'Security Guardrails', ext: 'preview', iconType: 'shield' },
            ],
        },
    },
    {
        id: 'p2sd', code: 'D',
        title: 'AI Operating Model & Governance',
        timing: 'CoE · RACI · Policies · Compliance',
        icon: (
            <svg viewBox="0 0 24 24"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
        ),
        detail: {
            frames: [
                { tag: 'Key Activities', items: ['AI CoE or federated model design and implementation', 'RACI definition and decision-making framework', 'Responsible AI policies and guardrail creation', 'Continuous prioritization and intake process design'] },
                { tag: 'Key Deliverables', items: ['AI Operating Model blueprint and org design', 'Governance framework with policies and controls', 'RACI matrix for AI decision-making', 'Intake and prioritization process for new use cases'] },
                { tag: 'Inputs Required', items: ['Current organizational structure and decision flows', 'Governance principles established in Discovery', 'Regulatory and compliance landscape', 'Executive sponsorship and transformation mandate'] },
                { tag: 'What You Get', value: 'AI that governs itself — not just runs.', desc: ' A sustainable operating model that scales AI adoption across the organization while maintaining control, compliance, and continuous improvement.' },
            ],
            examples: [
                { name: 'Operating Model Blueprint', ext: 'preview', iconType: 'doc' },
                { name: 'RACI Matrix', ext: 'preview', iconType: 'grid' },
                { name: 'AI Governance Policies', ext: 'preview', iconType: 'doc' },
            ],
        },
    },
    {
        id: 'p2se', code: 'E',
        title: 'ROI & Value Tracking',
        timing: 'Dashboards · Scorecards · Evidence',
        icon: (
            <svg viewBox="0 0 24 24"><path d="M18 20V10"/><path d="M12 20V4"/><path d="M6 20v-6"/></svg>
        ),
        detail: {
            frames: [
                { tag: 'Key Activities', items: ['Value tracking framework design and implementation', 'ROI dashboards with automated data collection', 'Savings, revenue uplift and productivity measurement', 'Evidence-based scaling and adjustment recommendations'] },
                { tag: 'Key Deliverables', items: ['ROI Tracking Dashboard (automated, real-time)', 'Value realization reports per use case', 'Executive scorecard for board reporting', 'Scaling recommendations based on evidence'] },
                { tag: 'Inputs Required', items: ['Financial Impact Model from Discovery', 'Baseline metrics per deployed use case', 'Access to business systems for data collection', 'Stakeholder definitions for reporting cadence'] },
                { tag: 'What You Get', value: 'Proof that AI pays for itself.', desc: ' Continuous tracking of ROI, savings, revenue uplift, productivity gains, and NPS impact — with evidence-based recommendations for scaling.' },
            ],
            examples: [
                { name: 'ROI Dashboard', ext: 'preview', iconType: 'chart' },
                { name: 'Executive Scorecard', ext: 'preview', iconType: 'grid' },
                { name: 'Value Realization Report', ext: 'preview', iconType: 'chat' },
            ],
        },
    },
];

/* ═══════════════════════════════════════════════════════════
   DATA — Why AI Stalls
═══════════════════════════════════════════════════════════ */
const STALL_CARDS = [
    {
        title: 'Fragmented Data',
        icon: (<svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>),
        desc: 'Siloed systems, no single source of truth, and data quality issues that block AI at every step. When AI models train on inconsistent, incomplete, or ungoverned data, the outputs are unreliable — and executives lose trust in the entire program.',
        implications: [
            'AI models produce unreliable outputs, eroding executive confidence',
            'Duplicate and conflicting customer records cause personalization to fail',
            'Compliance and audit risk increases without governed, traceable data',
            'Engineering teams spend 60–80% of time on data wrangling instead of model development',
        ],
        looksLike: [
            'Same customer appears as "Acme Corp" in CRM, "Acme Corporation" in billing, "ACME Inc." in contracts',
            'Marketing campaigns target customers who already churned — because churn data lives in a different system',
            'Finance team manually reconciles 3 different revenue reports every quarter',
            "Data science team can't reproduce model results because training data changes between runs",
        ],
        stats: [
            { value: '$12.9M', label: 'Average annual cost to enterprises due to poor data quality', source: 'IBM · Cost of Poor Data Quality, 2024' },
            { value: '73%', label: 'of enterprise data leaders identify data quality as the primary barrier to AI success', source: 'Forrester / Capital One Survey, 2024' },
        ],
        delay: '',
    },
    {
        title: 'Pilots That Never Scale',
        icon: (<svg viewBox="0 0 24 24"><path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2"/></svg>),
        desc: 'POCs that prove a point but never reach production. Organizations launch proof-of-concepts in safe sandboxes, but integration, governance, MLOps, and user adoption challenges mean they stall at the demo stage — forever.',
        implications: [
            'Millions invested in AI initiatives that never generate revenue or efficiency',
            'Pilot purgatory erodes board confidence in AI as a strategic investment',
            'Best engineering talent leaves, frustrated by projects that never ship',
            'Competitors who ship to production capture market advantage while you demo',
        ],
        looksLike: [
            '18-month "AI initiative" has 5 Jupyter notebooks and zero production deployments',
            'Data science team presents impressive accuracy metrics on test data; model has never seen real production traffic',
            'CTO reports "12 AI projects in progress" but none have touched a customer',
            "New AI vendor contract signed every quarter, each promising the previous one's missing capability",
        ],
        stats: [
            { value: '80%', label: 'of AI projects fail to reach meaningful production deployment', source: 'RAND Corporation, 2024' },
            { value: '30%', label: 'of GenAI projects will be abandoned after proof of concept by end of 2025', source: 'Gartner, July 2024' },
        ],
        delay: 'reveal-d1',
    },
    {
        title: 'No Economic Thesis',
        icon: (<svg viewBox="0 0 24 24"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>),
        desc: "AI initiatives disconnected from revenue, margin, or measurable business outcomes. Without a clear financial model from day one, projects compete for budget with no way to prove their value — and lose every time budgets tighten.",
        implications: [
            'AI budget gets cut first during cost optimization because ROI is unproven',
            'Board and CFO view AI as R&D expense, not strategic investment',
            'Teams optimize for technical metrics (accuracy, latency) instead of business outcomes',
            'No framework to compare or prioritize competing AI initiatives',
        ],
        looksLike: [
            'AI team reports "94% model accuracy" but nobody can say how that translates to revenue',
            'CFO asks "what\'s the ROI?" and the answer is a 40-slide deck with no numbers',
            "Use cases are selected based on what's technically interesting, not what moves the P&L",
            'Annual AI budget request is justified by "competitive necessity" instead of projected returns',
        ],
        stats: [
            { value: '95%', label: 'of enterprise AI pilot programs fail to deliver measurable financial returns', source: 'MIT · "The GenAI Divide", 2025' },
            { value: '42%', label: 'of companies abandoned most AI initiatives in 2025, up from 17% in 2024', source: 'MIT / Fortune, 2025' },
        ],
        delay: 'reveal-d2',
    },
    {
        title: 'Capability & Adoption Gaps',
        icon: (<svg viewBox="0 0 24 24"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>),
        desc: 'Technical execution without change management, governance structures, or user adoption strategy. Organizations invest in models and infrastructure but forget that AI only creates value when people actually use it — and when the organization is structured to sustain it.',
        implications: [
            "Deployed AI tools sit unused because end users don't trust or understand them",
            'No operating model means AI stays in pockets without organizational scale',
            'Talent gaps in MLOps and data engineering create bottlenecks at production',
            'Shadow AI proliferates without governance, creating compliance and security risk',
        ],
        looksLike: [
            'Customer service team trained for 2 hours on AI tool, adoption at 12% after 6 months',
            'Sales team bypasses AI recommendations and reverts to spreadsheets',
            'Individual departments hire their own AI vendors with no central coordination',
            'CISO discovers 14 unsanctioned LLM integrations processing customer data',
        ],
        stats: [
            { value: '70%', label: 'of AI resources should be invested in people and processes, not just technology', source: 'McKinsey, 2024' },
            { value: '43%', label: 'of organizations cite lack of skills as a top obstacle to AI success', source: 'Informatica CDO Insights, 2025' },
        ],
        delay: 'reveal-d3',
    },
];


/* ═══════════════════════════════════════════════════════════
   SVG ICON HELPERS
═══════════════════════════════════════════════════════════ */
const DocIcon = () => (
    <svg viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
);
const GridIcon = () => (
    <svg viewBox="0 0 24 24"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="9" y1="21" x2="9" y2="9"/></svg>
);
const ChartIcon = () => (
    <svg viewBox="0 0 24 24"><path d="M18 20V10"/><path d="M12 20V4"/><path d="M6 20v-6"/></svg>
);
const ChatIcon = () => (
    <svg viewBox="0 0 24 24"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
);
const ShieldIcon = () => (
    <svg viewBox="0 0 24 24"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
);

const ICON_MAP: Record<string, React.FC> = {
    doc: DocIcon,
    grid: GridIcon,
    chart: ChartIcon,
    chat: ChatIcon,
    shield: ShieldIcon,
};


/* ═══════════════════════════════════════════════════════════
   COMPONENT
═══════════════════════════════════════════════════════════ */
const Services: React.FC = () => {
    const { language, t } = useLanguage();
    const lp = (path: string) => `/${langToSlug(language)}${path}`;
    const [activeP1, setActiveP1] = useState('p1s1');
    const [activeP2, setActiveP2] = useState('p2sa');
    const [activeModal, setActiveModal] = useState<string | null>(null);
    const location = useLocation();
    useScrollReveal();

    // Sync URL hash → active tab + scroll to parent section
    useEffect(() => {
        const hash = location.hash.replace('#', '');
        if (!hash) return;

        // Determine target section and activate matching tab
        let targetId = hash;
        const p1Match = PHASE1_STEPS.find(s => s.id === hash);
        if (p1Match) {
            setActiveP1(p1Match.id);
            targetId = 'phase1';
        }
        const p2Match = PHASE2_MODULES.find(m => m.id === hash);
        if (p2Match) {
            setActiveP2(p2Match.id);
            targetId = 'phase2';
        }

        // Force-reveal elements between top of page and target so they have layout,
        // then scroll to target. Without this, .reveal elements have no height and
        // scrollIntoView lands in the wrong place.
        requestAnimationFrame(() => {
            const target = document.getElementById(targetId);
            if (!target) return;
            document.querySelectorAll('.reveal').forEach(el => {
                if (el.compareDocumentPosition(target) & Node.DOCUMENT_POSITION_FOLLOWING
                    || el === target || target.contains(el)) {
                    el.classList.add('visible');
                }
            });
            // Also reveal elements inside the target section
            target.querySelectorAll('.reveal').forEach(el => el.classList.add('visible'));
            target.scrollIntoView({ behavior: 'smooth' });
        });
    }, [location.hash]);

    const currentP1 = PHASE1_STEPS.find(s => s.id === activeP1) ?? PHASE1_STEPS[0];
    const currentP2 = PHASE2_MODULES.find(m => m.id === activeP2) ?? PHASE2_MODULES[0];

    return (
        <main>
            <SEOHead pageId="services" />
            {/* ═══════════════════════════ HERO ═══════════════════════════ */}
            <section className="svc-hero section" id="services-hero">
                <div className="container">
                    <div className="svc-hero-grid">
                        <div className="svc-hero-copy">
                            <div className="section-label reveal">{t('svc.heroLabel')}</div>
                            <h1 className="section-headline reveal" style={{ fontSize: 'clamp(32px,4.5vw,56px)', maxWidth: 560 }}>
                                {renderT(t('svc.heroHeadline'))}
                            </h1>
                            <p className="section-sub reveal">
                                {t('svc.heroSub')}
                            </p>
                            <div className="svc-hero-badges reveal">
                                <span className="svc-hero-badge ph1-badge">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
                                    {t('svc.heroBadge1')}
                                </span>
                                <span className="svc-hero-badge ph2-badge">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
                                    {t('svc.heroBadge2')}
                                </span>
                            </div>
                            <div className="reveal">
                                <Link to={lp('/contact#contact-form')} className="btn-primary">
                                    {t('cta.startDiscovery')}
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                                </Link>
                            </div>
                        </div>

                        {/* Right: Static frame SVG diagram */}
                        <div className="svc-hero-frame reveal">
                            <div className="svc-frame-bar">
                                <div className="dots"><div className="dot dot-r" /><div className="dot dot-y" /><div className="dot dot-g" /></div>
                                <span>perceptya-ai-value-system.svg</span>
                            </div>
                            <div className="svc-frame-body">
                                <svg viewBox="0 0 520 280" style={{ width: '100%', height: 'auto' }} xmlns="http://www.w3.org/2000/svg">
                                    <text x="260" y="18" textAnchor="middle" fill="rgba(255,255,255,0.2)" fontSize="9" fontFamily="DM Mono" letterSpacing="3">PERCEPTYA AI VALUE SYSTEM™</text>
                                    <rect x="10" y="32" width="220" height="234" rx="6" fill="rgba(255,107,43,0.04)" stroke="rgba(255,107,43,0.25)" strokeWidth="1"/>
                                    <text x="20" y="50" fill="#FF6B2B" fontSize="8" fontFamily="DM Mono" letterSpacing="1.5">PHASE 1 — AI VALUE DISCOVERY</text>
                                    <text x="210" y="50" textAnchor="end" fill="rgba(255,255,255,0.2)" fontSize="7" fontFamily="DM Mono">6–10 WKS</text>
                                    <rect x="20" y="60" width="200" height="36" rx="4" fill="#111" stroke="rgba(255,255,255,0.08)" strokeWidth="1"/><text x="30" y="74" fill="#FF6B2B" fontSize="7.5" fontFamily="DM Mono">01</text><text x="48" y="74" fill="rgba(255,255,255,0.6)" fontSize="8" fontFamily="Urbanist" fontWeight="500">Executive Alignment</text><text x="48" y="87" fill="rgba(255,255,255,0.25)" fontSize="7" fontFamily="DM Mono">Week 1</text>
                                    <rect x="20" y="102" width="200" height="36" rx="4" fill="#111" stroke="rgba(255,255,255,0.08)" strokeWidth="1"/><text x="30" y="116" fill="#FF6B2B" fontSize="7.5" fontFamily="DM Mono">02</text><text x="48" y="116" fill="rgba(255,255,255,0.6)" fontSize="8" fontFamily="Urbanist" fontWeight="500">AI &amp; Data Maturity Assessment</text><text x="48" y="129" fill="rgba(255,255,255,0.25)" fontSize="7" fontFamily="DM Mono">Weeks 2–4</text>
                                    <rect x="20" y="144" width="200" height="36" rx="4" fill="#111" stroke="rgba(255,255,255,0.08)" strokeWidth="1"/><text x="30" y="158" fill="#FF6B2B" fontSize="7.5" fontFamily="DM Mono">03</text><text x="48" y="158" fill="rgba(255,255,255,0.6)" fontSize="8" fontFamily="Urbanist" fontWeight="500">Use Case Prioritization</text><text x="48" y="171" fill="rgba(255,255,255,0.25)" fontSize="7" fontFamily="DM Mono">Weeks 4–6</text>
                                    <rect x="20" y="186" width="200" height="36" rx="4" fill="#111" stroke="rgba(255,255,255,0.08)" strokeWidth="1"/><text x="30" y="200" fill="#FF6B2B" fontSize="7.5" fontFamily="DM Mono">04</text><text x="48" y="200" fill="rgba(255,255,255,0.6)" fontSize="8" fontFamily="Urbanist" fontWeight="500">Architecture &amp; Roadmap</text><text x="48" y="213" fill="rgba(255,255,255,0.25)" fontSize="7" fontFamily="DM Mono">Weeks 6–10</text>
                                    <rect x="20" y="230" width="200" height="28" rx="4" fill="rgba(255,107,43,0.08)" stroke="rgba(255,107,43,0.2)" strokeWidth="1"/><text x="120" y="248" textAnchor="middle" fill="rgba(255,107,43,0.7)" fontSize="7" fontFamily="DM Mono" letterSpacing="0.5">5 EXECUTIVE DELIVERABLES</text>
                                    <line x1="240" y1="150" x2="280" y2="150" stroke="rgba(255,107,43,0.5)" strokeWidth="1.5" strokeDasharray="4,3"/><polygon points="278,145 288,150 278,155" fill="rgba(255,107,43,0.6)"/>
                                    <rect x="290" y="32" width="220" height="234" rx="6" fill="rgba(0,212,255,0.03)" stroke="rgba(0,212,255,0.2)" strokeWidth="1"/>
                                    <text x="300" y="50" fill="#00D4FF" fontSize="8" fontFamily="DM Mono" letterSpacing="1.5">PHASE 2 — INDUSTRIALIZATION</text>
                                    <text x="490" y="50" textAnchor="end" fill="rgba(255,255,255,0.2)" fontSize="7" fontFamily="DM Mono">3–12+ MO</text>
                                    <rect x="300" y="60" width="200" height="28" rx="4" fill="#111" stroke="rgba(0,212,255,0.12)" strokeWidth="1"/><text x="310" y="78" fill="#00D4FF" fontSize="7.5" fontFamily="DM Mono">A</text><text x="325" y="78" fill="rgba(255,255,255,0.6)" fontSize="8" fontFamily="Urbanist" fontWeight="500">Data Foundation</text>
                                    <rect x="300" y="94" width="200" height="28" rx="4" fill="#111" stroke="rgba(0,212,255,0.12)" strokeWidth="1"/><text x="310" y="112" fill="#00D4FF" fontSize="7.5" fontFamily="DM Mono">B</text><text x="325" y="112" fill="rgba(255,255,255,0.6)" fontSize="8" fontFamily="Urbanist" fontWeight="500">Use Case Implementation</text>
                                    <rect x="300" y="128" width="200" height="28" rx="4" fill="#111" stroke="rgba(0,212,255,0.12)" strokeWidth="1"/><text x="310" y="146" fill="#00D4FF" fontSize="7.5" fontFamily="DM Mono">C</text><text x="325" y="146" fill="rgba(255,255,255,0.6)" fontSize="8" fontFamily="Urbanist" fontWeight="500">Agent Architecture</text>
                                    <rect x="300" y="162" width="200" height="28" rx="4" fill="#111" stroke="rgba(0,212,255,0.12)" strokeWidth="1"/><text x="310" y="180" fill="#00D4FF" fontSize="7.5" fontFamily="DM Mono">D</text><text x="325" y="180" fill="rgba(255,255,255,0.6)" fontSize="8" fontFamily="Urbanist" fontWeight="500">Operating Model &amp; Governance</text>
                                    <rect x="300" y="196" width="200" height="28" rx="4" fill="#111" stroke="rgba(0,212,255,0.12)" strokeWidth="1"/><text x="310" y="214" fill="#00D4FF" fontSize="7.5" fontFamily="DM Mono">E</text><text x="325" y="214" fill="rgba(255,255,255,0.6)" fontSize="8" fontFamily="Urbanist" fontWeight="500">ROI &amp; Value Tracking</text>
                                    <rect x="300" y="232" width="200" height="28" rx="4" fill="rgba(0,255,133,0.06)" stroke="rgba(0,255,133,0.2)" strokeWidth="1"/><text x="400" y="250" textAnchor="middle" fill="rgba(0,255,133,0.7)" fontSize="7" fontFamily="DM Mono" letterSpacing="0.5">PRODUCTION AI + TRACKED ROI</text>
                                </svg>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ═══════════════════════════ PHASE 1 ═══════════════════════════ */}
            <section className="section" id="phase1">
                <div className="container">
                    <div className="phase-intro reveal">
                        <div className="section-label">{t('svc.p1Label')}</div>
                        <h2 className="section-headline">{renderT(t('svc.p1Headline'))}</h2>
                        <p className="section-sub">{t('svc.p1Sub')}</p>
                    </div>

                    <div className="phase-layout reveal" id="p1-layout">
                        {/* LEFT: Interactive Frame */}
                        <div className="phase-frame">
                            <div className="svc-frame-bar">
                                <div className="dots"><div className="dot dot-r" /><div className="dot dot-y" /><div className="dot dot-g" /></div>
                                <span>ai-value-discovery.process</span>
                            </div>
                            <div className="phase-frame-body">
                                <div className="phase-steps" id="p1-steps">
                                    {PHASE1_STEPS.map(step => (
                                        <button
                                            key={step.id}
                                            className={`phase-step${activeP1 === step.id ? ' active' : ''}`}
                                            onClick={() => setActiveP1(step.id)}
                                        >
                                            <div className="step-num">{step.num}</div>
                                            <div className="step-content">
                                                <div className="step-title">{t(`svc.${step.id}.title` as TranslationKey)}</div>
                                                <div className="step-timing">{t(`svc.${step.id}.timing` as TranslationKey)}</div>
                                            </div>
                                            <div className="step-icon">{step.icon}</div>
                                        </button>
                                    ))}
                                </div>
                                <div className="phase-bundle">
                                    <div className="phase-bundle-label">{t('svc.p1BundleLabel')}</div>
                                    <div className="phase-bundle-tags">
                                        <span className="phase-bundle-tag">{t('svc.p1BundleTag1')}</span>
                                        <span className="phase-bundle-tag">{t('svc.p1BundleTag2')}</span>
                                        <span className="phase-bundle-tag">{t('svc.p1BundleTag3')}</span>
                                        <span className="phase-bundle-tag">{t('svc.p1BundleTag4')}</span>
                                        <span className="phase-bundle-tag">{t('svc.p1BundleTag5')}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* RIGHT: Detail Panel */}
                        <DetailPanel
                            tag={t(`svc.${currentP1.id}.tag` as TranslationKey)}
                            title={t(`svc.${currentP1.id}.title` as TranslationKey)}
                            frames={currentP1.detail.frames}
                            examples={currentP1.detail.examples}
                            onPreview={(id) => setActiveModal(id)}
                        />
                    </div>
                </div>
            </section>

            {/* ═══════════════════════════ PHASE 2 ═══════════════════════════ */}
            <section className="section" id="phase2">
                <div className="container">
                    <div className="phase-intro reveal">
                        <div className="section-label">{t('svc.p2Label')}</div>
                        <h2 className="section-headline">{renderT(t('svc.p2Headline'))}</h2>
                        <p className="section-sub">{t('svc.p2Sub')}</p>
                    </div>

                    <div className="phase-layout reveal" id="p2-layout">
                        {/* LEFT: Interactive Frame */}
                        <div className="phase-frame">
                            <div className="svc-frame-bar">
                                <div className="dots"><div className="dot dot-r" /><div className="dot dot-y" /><div className="dot dot-g" /></div>
                                <span>ai-industrialization.modules</span>
                            </div>
                            <div className="phase-frame-body">
                                <div className="phase-steps" id="p2-steps">
                                    {PHASE2_MODULES.map(mod => (
                                        <button
                                            key={mod.id}
                                            className={`phase-step${activeP2 === mod.id ? ' active' : ''}`}
                                            onClick={() => setActiveP2(mod.id)}
                                        >
                                            <div className="step-num">{mod.code}</div>
                                            <div className="step-content">
                                                <div className="step-title">{t(`svc.${mod.id}.title` as TranslationKey)}</div>
                                                <div className="step-timing">{t(`svc.${mod.id}.timing` as TranslationKey)}</div>
                                            </div>
                                            <div className="step-icon">{mod.icon}</div>
                                        </button>
                                    ))}
                                </div>
                                <div className="phase-bundle">
                                    <div className="phase-bundle-label">{t('svc.p2BundleLabel')}</div>
                                    <div className="phase-bundle-tags">
                                        <span className="phase-bundle-tag">{t('svc.p2BundleTag1')}</span>
                                        <span className="phase-bundle-tag">{t('svc.p2BundleTag2')}</span>
                                        <span className="phase-bundle-tag">{t('svc.p2BundleTag3')}</span>
                                        <span className="phase-bundle-tag">{t('svc.p2BundleTag4')}</span>
                                        <span className="phase-bundle-tag">{t('svc.p2BundleTag5')}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* RIGHT: Detail Panel */}
                        <DetailPanel
                            tag={t(`svc.${currentP2.id}.tag` as TranslationKey)}
                            title={t(`svc.${currentP2.id}.title` as TranslationKey)}
                            frames={currentP2.detail.frames}
                            examples={currentP2.detail.examples}
                            onPreview={(id) => setActiveModal(id)}
                        />
                    </div>
                </div>
            </section>

            {/* ═══════════════════════════ ENGAGEMENT MODEL ═══════════════════════════ */}
            <section className="section" id="engagement">
                <div className="container">
                    <div className="reveal">
                        <div className="section-label">{t('svc.engLabel')}</div>
                        <h2 className="section-headline">{t('svc.engHeadline')}</h2>
                        <p className="section-sub">{t('svc.engSub')}</p>
                    </div>
                    <div className="eng-grid">
                        <div className="eng-card reveal">
                            <div className="eng-card-num">01</div>
                            <div className="eng-card-badge discovery">{t('svc.eng1Badge')}</div>
                            <div className="eng-card-title">{t('svc.eng1Title')}</div>
                            <div className="eng-card-body">{t('svc.eng1Body')}</div>
                            <div className="eng-card-meta">
                                <span className="eng-meta-tag">{t('svc.eng1Meta1')}</span>
                                <span className="eng-meta-tag">{t('svc.eng1Meta2')}</span>
                                <span className="eng-meta-tag">{t('svc.eng1Meta3')}</span>
                            </div>
                        </div>
                        <div className="eng-card reveal reveal-d1">
                            <div className="eng-card-num">02</div>
                            <div className="eng-card-badge industrial">{t('svc.eng2Badge')}</div>
                            <div className="eng-card-title">{t('svc.eng2Title')}</div>
                            <div className="eng-card-body">{t('svc.eng2Body')}</div>
                            <div className="eng-card-meta">
                                <span className="eng-meta-tag">{t('svc.eng2Meta1')}</span>
                                <span className="eng-meta-tag">{t('svc.eng2Meta2')}</span>
                                <span className="eng-meta-tag">{t('svc.eng2Meta3')}</span>
                            </div>
                        </div>
                        <div className="eng-card reveal reveal-d2">
                            <div className="eng-card-num">03</div>
                            <div className="eng-card-badge pods">{t('svc.eng3Badge')}</div>
                            <div className="eng-card-title">{t('svc.eng3Title')}</div>
                            <div className="eng-card-body">{t('svc.eng3Body')}</div>
                            <div className="eng-card-meta">
                                <span className="eng-meta-tag">{t('svc.eng3Meta1')}</span>
                                <span className="eng-meta-tag">{t('svc.eng3Meta2')}</span>
                                <span className="eng-meta-tag">{t('svc.eng3Meta3')}</span>
                            </div>
                        </div>
                    </div>
                    <div style={{ marginTop: 48 }} className="reveal">
                        <Link to={lp('/contact#contact-form')} className="btn-primary">
                            {t('svc.engCta')}
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                        </Link>
                    </div>
                </div>
            </section>

            {/* ═══════════════════════════ WHY AI STALLS ═══════════════════════════ */}
            <section className="section" id="why-stall">
                <div className="container">
                    <div className="stall-intro reveal">
                        <div className="section-label">{t('svc.stallLabel')}</div>
                        <h2 className="section-headline">{renderT(t('svc.stallHeadline'))}</h2>
                        <p className="section-sub">{t('svc.stallSub')}</p>
                    </div>

                    <div className="stall-cards">
                        {STALL_CARDS.map((card, cardIdx) => (
                            <div key={card.title} className={`stall-card reveal ${card.delay}`}>
                                <div className="stall-card-header">
                                    <div className="stall-icon">{card.icon}</div>
                                    <div className="stall-card-info">
                                        <div className="stall-card-title">{t(`svc.stall${cardIdx + 1}.title` as TranslationKey)}</div>
                                        <div className="stall-card-desc">{t(`svc.stall${cardIdx + 1}.desc` as TranslationKey)}</div>
                                    </div>
                                </div>
                                <div className="stall-card-body">
                                    <div>
                                        <div className="stall-col-tag red">{t('svc.stallImplications')}</div>
                                        <ul className="stall-col-list red">
                                            {card.implications.map((li, i) => <li key={i}>{li}</li>)}
                                        </ul>
                                    </div>
                                    <div>
                                        <div className="stall-col-tag muted-tag">{t('svc.stallLooksLike')}</div>
                                        <ul className="stall-col-list">
                                            {card.looksLike.map((li, i) => <li key={i}>{li}</li>)}
                                        </ul>
                                    </div>
                                    <div>
                                        <div className="stall-col-tag">{t('svc.stallResearch')}</div>
                                        {card.stats.map((stat, i) => (
                                            <div key={i} className="stall-stat" style={i > 0 ? { marginTop: 8 } : undefined}>
                                                <div className="stall-stat-value">{stat.value}</div>
                                                <div className="stall-stat-label">{stat.label}</div>
                                                <div className="stall-stat-source">{stat.source}</div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ═══════════════════════════ CTA STRIP ═══════════════════════════ */}
            <section className="svc-cta-strip" id="svc-cta">
                <div className="container">
                    <div className="section-label reveal" style={{ justifyContent: 'center' }}>{t('cta.startHere')}</div>
                    <h2 className="svc-cta-headline reveal">
                        {renderT(t('cta.readyHeadline'))}
                    </h2>
                    <p className="svc-cta-sub reveal">
                        {t('cta.readySub')}
                    </p>
                    <div className="svc-cta-actions reveal">
                        <Link to={lp('/contact#contact-form')} className="btn-primary">
                            {t('cta.startDiscovery')}
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                        </Link>
                        <Link to={lp('/manifesto')} className="btn-secondary">{t('cta.readManifesto')}</Link>
                    </div>
                </div>
            </section>

            {/* ── Deliverable Preview Modals ── */}
            <DeliverableModals activeModal={activeModal} onClose={() => setActiveModal(null)} />
        </main>
    );
};


/* ═══════════════════════════════════════════════════════════
   DetailPanel — Reusable panel for Phase 1 steps & Phase 2 modules
═══════════════════════════════════════════════════════════ */
interface FrameData {
    tag: string;
    items?: string[];
    value?: string;
    desc?: string;
}

interface ExampleData {
    name: string;
    ext: string;
    iconType: string;
}

interface DetailPanelProps {
    tag: string;
    title: string;
    frames: FrameData[];
    examples: ExampleData[];
    onPreview?: (modalId: string) => void;
}

const FRAME_TAG_MAP: Record<string, TranslationKey> = {
    'Inputs': 'svc.frameInputs' as TranslationKey,
    'Key Activities': 'svc.frameActivities' as TranslationKey,
    'Key Deliverables': 'svc.frameDeliverables' as TranslationKey,
    'What You Get': 'svc.frameWhatYouGet' as TranslationKey,
    'Inputs Required': 'svc.frameInputsRequired' as TranslationKey,
};

const DetailPanel: React.FC<DetailPanelProps> = ({ tag, title, frames, examples, onPreview }) => {
    const { t } = useLanguage();
    return (
    <div className="phase-detail open">
        <div className="pd-header">
            <span className="pd-tag">{tag}</span>
            <div className="pd-title">{title}</div>
        </div>
        <div className="pd-body">
            <div className="pd-frames">
                {frames.map((frame, i) => (
                    <div key={i} className="pd-f">
                        <div className="pd-f-tag">{FRAME_TAG_MAP[frame.tag] ? t(FRAME_TAG_MAP[frame.tag]) : frame.tag}</div>
                        {frame.items ? (
                            <ul className="pd-f-list">
                                {frame.items.map((item, j) => <li key={j}>{item}</li>)}
                            </ul>
                        ) : (
                            <p className="pd-f-value">
                                <strong>{frame.value}</strong>{frame.desc}
                            </p>
                        )}
                    </div>
                ))}
            </div>
        </div>
        <div className="pd-examples">
            <div className="pd-examples-label">Example Deliverables</div>
            <div className="pd-examples-grid">
                {examples.map((ex, i) => {
                    const IconComp = ICON_MAP[ex.iconType] ?? DocIcon;
                    const modalId = MODAL_MAP[ex.name];
                    return (
                        <div
                            key={i}
                            className="pd-example-item"
                            data-preview={modalId || undefined}
                            onClick={modalId && onPreview ? () => onPreview(modalId) : undefined}
                            style={modalId ? { cursor: 'pointer' } : undefined}
                        >
                            <div className="pd-example-icon"><IconComp /></div>
                            <div>
                                <div className="pd-example-name">{ex.name}</div>
                                <div className="pd-example-ext">{ex.ext}</div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    </div>
    );
};


export default Services;
